// Shared unread-aggregation helpers.
//
// Keys in the `unread` map are fully-qualified `space:channel` ids built by
// ChatLayout's fq/k helpers (e.g. `space123:general`, `dm_abc:chat`). A space's
// unread total is therefore the sum of every key whose `space:` prefix matches.
// Centralising the prefix logic here keeps the spaces header (MobileHeader) and
// the desktop rail (SpaceRail) from drifting apart and pins the coupling to the
// key format to a single place.

type UnreadMap = Record<string, number>;

/**
 * One pass over the unread map → per-space totals. Cheaper than filtering the
 * whole map once per space avatar; callers memoise this on `unread` and then do
 * O(1) lookups (`totals[spaceId] || 0`).
 */
export function spaceUnreadTotals(unread: UnreadMap): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const key in unread) {
    const sep = key.indexOf(":");
    if (sep <= 0) continue;
    const spaceId = key.slice(0, sep);
    totals[spaceId] = (totals[spaceId] || 0) + (unread[key] || 0);
  }
  return totals;
}

/**
 * Whether any DM conversation has unread messages — drives the green dot on the
 * DM icon in the header and the rail.
 *
 * Count-only, matching the per-space dots. We deliberately do NOT consult the
 * `notified` throttle map here: it is not cleared on a cross-device per-channel
 * read (only the global `unread:reset` wipes it), so keying a presence dot off it
 * would leave the dot lit after the user has already read the DM elsewhere.
 */
export function anyDmUnread(voids: { id: string }[], unread: UnreadMap): boolean {
  return voids.some(
    (v) => String(v.id).startsWith("dm_") && (unread[`${v.id}:chat`] || 0) > 0,
  );
}
